import moment from "moment-timezone";
import { CommonDestinationTimezone } from "../utils";
import { Commodities } from "./commodity";
import { CropYear } from "@lhc/commodities/dates";
import { getOptionMonthComboBase, optionLetterToMonthNumber } from "./common";

/**
 * Derives the shipment start/end dates for a SHORT-DATED option month (one
 * that starts with "O", e.g. "OZ5" / "ON6").
 * Its trying to locate the month of the new crop.
 *
 * For a short-dated option the shipment window is the commodity's new-crop
 * month for the derived crop year. The crop year advances by one once the
 * option month reaches the new-crop month letter for that commodity:
 *
 *   cropYear = optionCalendarYear + (optionMonthNumber >= newCropMonthNumber ? 1 : 0)
 *
 * e.g. Corn (new crop = Z/December):
 *   OZ5 (Dec 2025) -> 2026-12-01 .. 2026-12-31
 *   ON6 (Jul 2026) -> 2026-12-01 .. 2026-12-31
 *   OZ6 (Dec 2026) -> 2027-12-01 .. 2027-12-31
 *
 * Returns `undefined` when:
 *   - the option month is NOT short-dated (does not start with "O"),
 *   - the option month cannot be parsed,
 *   - the commodity has no new-crop rule, or
 *   - the new-crop month letter is unknown.
 */
export function deriveShipmentDatesFromShortDatedOptionMonth(
  commodity: Commodities | string | undefined,
  optionMonth: string | undefined,
): { shipment_start_date: Date; shipment_end_date: Date } | undefined {
  if (!commodity || !optionMonth) return undefined;

  const rule = CropYear.COMMODITY_RULES[commodity];
  if (!rule) return undefined;

  const newCropLetter = rule.newCropOptionMonths.values().next().value;
  if (!newCropLetter) return undefined;
  const newCropMonthNumber = optionLetterToMonthNumber[newCropLetter];
  if (!newCropMonthNumber) return undefined;

  let base;
  try {
    base = getOptionMonthComboBase(optionMonth);
  } catch {
    return undefined;
  }
  if (!base.isShortDated) return undefined;

  const optionCalendarYear = parseInt(base.fullYear);
  const cropYear =
    optionCalendarYear + (base.monthNumber >= newCropMonthNumber ? 1 : 0);

  const startMoment = moment
    .tz(CommonDestinationTimezone)
    .year(cropYear)
    .month(newCropMonthNumber - 1)
    .date(1)
    .startOf("day");
  const endMoment = moment(startMoment).endOf("month");

  return {
    shipment_start_date: startMoment.toDate(),
    shipment_end_date: endMoment.toDate(),
  };
}
